import classNames from "classnames";
import type { ReactNode } from "react";
import type React from "react";

import { Stack } from "../../layouts/Stack/Stack";

import style from "./Sidebar.module.css"; // eslint-disable-line postcss-modules/no-unused-class

type SidebarComponent<T> = React.FC<T> & {
  Item: typeof SidebarItem;
  NavItem: typeof SidebarNavItem;
  SpacerItem: typeof SidebarSpacerItem;
  Space: typeof SidebarSpace;
  Header: typeof SidebarHeader;
  Divider: typeof SidebarDivider;
  ScrollContainer: typeof SidebarScrollContainer;
};

export const Sidebar: SidebarComponent<{
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
}> = ({ children, className, style }) => {
  return (
    <Stack role="menubar" className={className} direction={"column"} style={style}>
      {children}
    </Stack>
  );
};

const SidebarItem: React.FC<{
  children: ReactNode;
  selected?: boolean;
  className?: string;
  onClick?: (event?: React.MouseEvent) => void;
}> = ({ children, selected, className, onClick }) => {
  return (
    <div
      role="menuitem"
      className={classNames(style.sidebarItem, selected && style.selected, className)}
      onClick={(event: React.MouseEvent) => onClick?.(event)}
    >
      {children}
    </div>
  );
};
Sidebar.Item = SidebarItem;

const SidebarNavItem: React.FC<{
  icon?: ReactNode;
  children: ReactNode;
  selected?: boolean;
  className?: string;
  onClick?: (event?: React.MouseEvent) => void;
}> = ({ children, className, icon, ...props }) => {
  return (
    <SidebarItem className={classNames(style.sidebarNavItem, className)} {...props}>
      {icon && <span className={style.sidebarNavItemIcon}>{icon}</span>}
      {children}
    </SidebarItem>
  );
};
Sidebar.NavItem = SidebarNavItem;

const SidebarSpacerItem: React.FC<{ children: ReactNode; variant?: "top" | "bottom" | "both" }> = ({
  children,
  variant = "both",
}) => {
  return (
    <div role="menuitem" className={classNames(style.sidebarSpacerItem, style[variant])}>
      {children}
    </div>
  );
};
Sidebar.SpacerItem = SidebarSpacerItem;

const SidebarSpace: React.FC<{
  marginTop?: React.CSSProperties["marginTop"];
  marginBottom?: React.CSSProperties["marginBottom"];
}> = ({ marginTop, marginBottom }) => {
  return <div style={{ marginTop, marginBottom }}></div>;
};
Sidebar.Space = SidebarSpace;

const SidebarScrollContainer: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <div className={style.scrollContainer}>{children}</div>;
};
Sidebar.ScrollContainer = SidebarScrollContainer;

const SidebarHeader: React.FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return <header className={classNames(style.sidebarHeader, className)}>{children}</header>;
};
Sidebar.Header = SidebarHeader;

const SidebarDivider: React.FC = () => {
  return <hr className={style.sidebarDivider}></hr>;
};
Sidebar.Divider = SidebarDivider;
